import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { obtenerEvaluaciones, obtenerReporteProgreso, obtenerReporteResumen } from '../../services/evaluacionesService';
import { obtenerCentro } from '../../services/centroService';
import EvaluationCharts from '../../components/EvaluationCharts';
import { formatearFechaLocal } from '../../utils/dateUtils';
import { PDFDownloadLink, Document, Page, Text, View, StyleSheet, Image, Svg, Line, Rect, G } from '@react-pdf/renderer';
import { FileDown, PieChart,User, ChevronRight,CalendarDays } from 'lucide-react';
import LogoJireh from '../../assets/logo.jpg';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';



// Colores para las gráficas
const COLORS = ['#6366F1', '#EC4899', '#10B981', '#F59E0B', '#8B5CF6'];

// Función para determinar el nivel de logro
const determinarLogroEquivalente = (porcentaje) => {
   if (porcentaje <= 66.00) return "NO LO CONSIGUE";
   if (porcentaje <= 86.00) return "EN PROCESO";
   if (porcentaje < 101.00) return "DOMINA EL LOGRO";
   return "DOMINA EL LOGRO";
};

// Función para calcular la edad
const calcularEdad = (fechaNacimiento) => {
   if (!fechaNacimiento) return 'No especificada';
   
   const fechaNac = new Date(fechaNacimiento);
   const hoy = new Date();
   
   let años = hoy.getFullYear() - fechaNac.getFullYear();
   let meses = hoy.getMonth() - fechaNac.getMonth();
   
   if (meses < 0 || (meses === 0 && hoy.getDate() < fechaNac.getDate())) {
       años--;
       meses = 12 + meses;
   }
   
   // Ajustar meses si el día actual es menor que el día de nacimiento
   if (hoy.getDate() < fechaNac.getDate()) {
       meses--;
   }
   
   // Formatear el resultado
   if (años < 1) {
       return `${meses} meses`;
   } else if (años === 1) {
       return meses === 0 ? 
           '1 año' : 
           `1 año y ${meses} ${meses === 1 ? 'mes' : 'meses'}`;
   } else {
       return meses === 0 ? 
           `${años} años` : 
           `${años} años y ${meses} ${meses === 1 ? 'mes' : 'meses'}`;
   }
};

// Estilos para el PDF
const styles = StyleSheet.create({
    page: {
        padding: 20,
        backgroundColor: '#FFFFFF',
        fontSize: 9
    },
    header: {
        marginBottom: 15,
        textAlign: 'center',
        borderWidth: 1,
        borderColor: '#000000',
        padding: 8,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center'
    },
    headerLogo: {
        width: 50,
        height: 50,
        marginRight: 8
    },
    headerContent: {
        flex: 1,
        textAlign: 'center'
    },
    headerTitle: {
        fontSize: 12,
        fontWeight: 'bold',
        textAlign: 'center',
        marginBottom: 4
    },
    headerSubtitle: {
        fontSize: 10,
        textAlign: 'center',
        marginBottom: 4
    },
    section: {
        marginBottom: 12,
        borderWidth: 1,
        borderColor: '#000000',
        padding: 6
    },
    sectionTitle: {
        fontSize: 10,
        fontWeight: 'bold',
        marginBottom: 8,
        backgroundColor: '#E5E7EB',
        padding: 4,
        borderBottomWidth: 1,
        borderBottomColor: '#000000'
    },
    infoGrid: {
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 6
    },
    infoRow: {
        flexDirection: 'row',
        marginBottom: 4,
        borderBottomWidth: 1,
        borderBottomColor: '#E5E7EB',
        paddingVertical: 2
    },
    infoLabel: {
        width: 110,
        fontWeight: 'bold',
        fontSize: 8
    },
    infoValue: {
        flex: 1,
        fontSize: 8
    },
    ambitosTable: {
        width: '100%',
        marginBottom: 12,
        borderWidth: 1,
        borderColor: '#000000'
    },
    ambitosHeader: {
        flexDirection: 'row',
        backgroundColor: '#E5E7EB',
        borderBottomWidth: 1,
        borderBottomColor: '#000000'
    },
    ambitosHeaderCell: {
        padding: 3,
        flex: 1,
        textAlign: 'center',
        fontWeight: 'bold',
        fontSize: 8,
        borderRightWidth: 1,
        borderRightColor: '#000000'
    },
    ambitosRow: {
        flexDirection: 'row',
        borderBottomWidth: 1,
        borderBottomColor: '#000000',
        minHeight: 20
    },
    ambitosCell: {
        padding: 3,
        flex: 1,
        textAlign: 'left',
        fontSize: 7,
        borderRightWidth: 1,
        borderRightColor: '#000000'
    },
    chartSection: {
        marginTop: 10,
        padding: 10,
        backgroundColor: '#FFFFFF',
        borderWidth: 1,
        borderColor: '#000000'
    },
    chartTitle: {
        fontSize: 11,
        fontWeight: 'bold',
        marginBottom: 10,
        textAlign: 'center',
        backgroundColor: '#E5E7EB',
        padding: 4,
        borderBottomWidth: 1,
        borderBottomColor: '#000000'
    },
    barChartContainer: {
        width: '100%',
        maxHeight: 200,
        marginBottom: 10
    },
    barLabel: {
        fontSize: 8,
        textAlign: 'right',
        marginRight: 5,
        width: 120
    },
    percentageLabel: {
        fontSize: 8,
        marginLeft: 5,
        width: 40
    },
    resumenTable: {
        width: '100%',
        borderWidth: 1,
        borderColor: '#000000',
        marginTop: 10
    },
    resumenRow: {
        flexDirection: 'row',
        borderBottomWidth: 1,
        borderBottomColor: '#000000'
    },
    resumenCell: {
        padding: 4,
        flex: 1,
        fontSize: 8,
        textAlign: 'center',
        borderRightWidth: 1,
        borderRightColor: '#000000'
    },
    equivalenciasSection: {
        marginBottom: 12,
        padding: 6
    },
    equivalenciasTitle: {
        fontSize: 10,
        fontWeight: 'bold',
        marginBottom: 8,
        color: '#1F2937'
    },
    equivalenciasGrid: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        gap: 8
    },
    equivalenciaItem: {
        flex: 1,
        padding: 6,
        borderRadius: 4
    },
    equivalenciaText: {
        fontSize: 8,
        color: '#4B5563'
    },
    equivalenciaNumero: {
        fontWeight: 'bold',
        marginRight: 3
    }
});
// Componente de Gráfica de Barras para PDF mejorado
const PDFBarChart = ({ data }) => {
   const maxWidth = 300; // Reducido para evitar desbordamiento
   const barHeight = 20;
   const spacing = 8;
   const maxPercentage = 100;

   return (
       <View style={styles.barChartContainer}>
           {data.map((item, index) => (
               <View key={index} style={{ flexDirection: 'row', marginBottom: spacing, alignItems: 'center' }}>
                   <Text style={styles.barLabel} numberOfLines={2}>{item.name}</Text>
                   <View style={{ flex: 1, flexDirection: 'row', alignItems: 'center' }}>
                       <Svg width={maxWidth} height={barHeight}>
                           <Rect
                               x={0}
                               y={0}
                               width={(item.porcentaje / maxPercentage) * maxWidth}
                               height={barHeight}
                               fill={COLORS[index % COLORS.length]}
                               rx={2}
                               ry={2}
                           />
                       </Svg>
                       <Text style={styles.percentageLabel}>{`${item.porcentaje.toFixed(1)}%`}</Text>
                   </View>
               </View>
           ))}
       </View>
   );
};

// Componente PDF mejorado
const ReportePDF = ({ centro, nino, evaluacion, reportes }) => {
   const datosGrafica = reportes?.datosReporteProgreso?.data?.map((ambito, index) => ({
       name: ambito.ambito_aprendizaje,
       porcentaje: parseFloat(ambito.porcentaje_ambito),
       fill: COLORS[index % COLORS.length]
   })) || [];

   return (
       <Document>
           <Page size="A4" style={styles.page} wrap>
               {/* Encabezado */}
               <View style={styles.header} fixed>
                   <Image 
                       src={LogoJireh}
                       style={styles.headerLogo}
                   />
                   <View style={styles.headerContent}>
                       <Text style={styles.headerTitle}>
                           {`CENTRO DE DESARROLLO INFANTIL "${centro?.nombre_unidad?.toUpperCase() || 'SIN NOMBRE'}"`}
                       </Text>
                       <Text style={styles.headerSubtitle}>FICHA DE INDICADORES DEL DESARROLLO{"\n"}INFANTIL INTEGRAL</Text>
                   </View>
               </View>

               {/* Equivalencias de Nivel de Logro */}
               <View style={styles.equivalenciasSection} wrap={false}>
                   <Text style={styles.equivalenciasTitle}>Equivalencias de Nivel de Logro</Text>
                   <View style={styles.equivalenciasGrid}>
                       <View style={[styles.equivalenciaItem, { backgroundColor: '#FEE2E2' }]}>
                           <Text style={styles.equivalenciaText}>
                               <Text style={styles.equivalenciaNumero}>1:</Text> No lo consigue
                           </Text>
                       </View>
                       <View style={[styles.equivalenciaItem, { backgroundColor: '#FEF9C3' }]}>
                           <Text style={styles.equivalenciaText}>
                               <Text style={styles.equivalenciaNumero}>2:</Text> En proceso
                           </Text>
                       </View>
                       <View style={[styles.equivalenciaItem, { backgroundColor: '#DCFCE7' }]}>
                           <Text style={styles.equivalenciaText}>
                               <Text style={styles.equivalenciaNumero}>3:</Text> Domina el logro
                           </Text>
                       </View>
                   </View>
               </View>

               {/* Sección 1: Datos del Centro */}
               <View style={styles.section} wrap={false}>
                   <Text style={styles.sectionTitle}>1. DATOS DEL CENTRO:</Text>
                   <View style={styles.infoGrid}>
                       <View style={[styles.infoRow, { width: '48%' }]}>
                           <Text style={styles.infoLabel}>ZONA:</Text>
                           <Text style={styles.infoValue}>{centro?.zona}</Text>
                       </View>
                       <View style={[styles.infoRow, { width: '48%' }]}>
                           <Text style={styles.infoLabel}>CANTÓN:</Text>
                           <Text style={styles.infoValue}>{centro?.canton}</Text>
                       </View>
                       <View style={[styles.infoRow, { width: '48%' }]}>
                           <Text style={styles.infoLabel}>PROVINCIA:</Text>
                           <Text style={styles.infoValue}>{centro?.provincia}</Text>
                       </View>
                       <View style={[styles.infoRow, { width: '48%' }]}>
                           <Text style={styles.infoLabel}>PARROQUIA:</Text>
                           <Text style={styles.infoValue}>{centro?.parroquia}</Text>
                       </View>
                       <View style={[styles.infoRow, { width: '100%' }]}>
                           <Text style={styles.infoLabel}>NOMBRE DE LA UNIDAD:</Text>
                           <Text style={styles.infoValue}>{centro?.nombre_unidad}</Text>
                       </View>
                   </View>
               </View>

               {/* Sección 2: Datos del Niño */}
               <View style={styles.section} wrap={false}>
                   <Text style={styles.sectionTitle}>2. DATOS DEL NIÑO/A:</Text>
                   <View style={styles.infoGrid}>
                       <View style={[styles.infoRow, { width: '100%' }]}>
                           <Text style={styles.infoLabel}>NOMBRES Y APPELLIDOS:</Text>
                           <Text style={styles.infoValue}>{nino?.nombre} {nino?.apellidos}</Text>
                       </View>
                       <View style={[styles.infoRow, { width: '48%' }]}>
                           <Text style={styles.infoLabel}>CÉDULA:</Text>
                           <Text style={styles.infoValue}>{nino?.cedula || 'No registrada'}</Text>
                       </View>
                       <View style={[styles.infoRow, { width: '48%' }]}>
                           <Text style={styles.infoLabel}>FECHA DE NACIMIENTO:</Text>
                           <Text style={styles.infoValue}>{new Date(nino?.fecha_nacimiento).toLocaleDateString()}</Text>
                       </View>
                       <View style={[styles.infoRow, { width: '48%' }]}>
                           <Text style={styles.infoLabel}>EDAD AL MOMENTO DE EVALUACIÓN:</Text>
                           <Text style={styles.infoValue}>{calcularEdad()}</Text>
                       </View>
                       <View style={[styles.infoRow, { width: '48%' }]}>
                           <Text style={styles.infoLabel}>SECCIÓN:</Text>
                           <Text style={styles.infoValue}>{nino?.seccion || 'No asignada'}</Text>
                       </View>
                       <View style={[styles.infoRow, { width: '48%' }]}>
                           <Text style={styles.infoLabel}>FECHA DE EVALUACIÓN:</Text>
                           <Text style={styles.infoValue}>{new Date(evaluacion?.fecha).toLocaleDateString()}</Text>
                       </View>
                   </View>
               </View>

               {/* Sección 3: Registro de Indicadores */}
               <View style={styles.section} wrap={false}>
                   <Text style={styles.sectionTitle}>3. REGISTRO DE INDICADORES</Text>
                   
                   {/* Grupo de edad */}
                   <View style={[styles.infoRow, { marginBottom: 10, borderBottomWidth: 2 }]}>
                       <Text style={[styles.infoLabel, { fontSize: 9, color: '#1F2937' }]}>GRUPO DE EDAD:</Text>
                       <Text style={[styles.infoValue, { fontSize: 9, fontWeight: 'bold', color: '#1F2937' }]}>
                           {evaluacion?.grupo_edad || 'No especificado'}
                       </Text>
                   </View>

                   <View style={styles.ambitosTable}>
                       <View style={styles.ambitosHeader} fixed>
                           <Text style={[styles.ambitosHeaderCell, { flex: 0.3 }]}>No.</Text>
                           <Text style={[styles.ambitosHeaderCell, { flex: 1.5 }]}>ÁMBITOS DEL APRENDIZAJE</Text>
                           <Text style={[styles.ambitosHeaderCell, { flex: 2.5 }]}>INDICADORES</Text>
                           <Text style={[styles.ambitosHeaderCell, { flex: 0.5, borderRightWidth: 0 }]}>NIVEL</Text>
                       </View>
                       {evaluacion?.ambitos?.map((ambito, index) => (
                           <View key={index} style={styles.ambitosRow} wrap={false}>
                               <Text style={[styles.ambitosCell, { flex: 0.3 }]}>{index + 1}</Text>
                               <Text style={[styles.ambitosCell, { flex: 1.5 }]}>{ambito.ambito_aprendizaje}</Text>
                               <Text style={[styles.ambitosCell, { flex: 2.5 }]}>{ambito.indicador}</Text>
                               <Text style={[styles.ambitosCell, { flex: 0.5, borderRightWidth: 0, textAlign: 'center' }]}>{ambito.nivel_logro}</Text>
                           </View>
                       ))}
                   </View>
               </View>

               {/* Sección 4: Resultados */}
               <View style={[styles.section, { marginTop: 15 }]} wrap={false}>
                   <Text style={styles.sectionTitle}>4. RESULTADOS DE LA EVALUACIÓN</Text>
                   <View style={styles.chartSection}>
                       <Text style={styles.chartTitle}>PROGRESO POR ÁMBITOS</Text>
                       <PDFBarChart data={datosGrafica} />
                   </View>

                   <View style={styles.resumenTable}>
                       <View style={styles.ambitosHeader}>
                           <Text style={[styles.ambitosHeaderCell, { flex: 2 }]}>ÁMBITO</Text>
                           <Text style={[styles.ambitosHeaderCell, { flex: 1 }]}>PORCENTAJE</Text>
                           <Text style={[styles.ambitosHeaderCell, { flex: 1.5, borderRightWidth: 0 }]}>NIVEL DE LOGRO</Text>
                       </View>
                       {datosGrafica.map((item, index) => (
                           <View key={index} style={styles.ambitosRow}>
                               <Text style={[styles.ambitosCell, { flex: 2 }]}>{item.name}</Text>
                               <Text style={[styles.ambitosCell, { flex: 1, textAlign: 'center' }]}>
                                   {item.porcentaje.toFixed(1)}%
                               </Text>
                               <Text style={[styles.ambitosCell, { flex: 1.5, borderRightWidth: 0, textAlign: 'center' }]}>
                                   {determinarLogroEquivalente(item.porcentaje)}
                               </Text>
                           </View>
                       ))}
                       {reportes?.datosReporteResumen?.data && (
                           <View style={[styles.ambitosRow, { backgroundColor: '#F3F4F6' }]}>
                               <Text style={[styles.ambitosCell, { flex: 2, fontWeight: 'bold' }]}>RESUMEN GENERAL</Text>
                               <Text style={[styles.ambitosCell, { flex: 1, textAlign: 'center', fontWeight: 'bold' }]}>
                                   {parseFloat(reportes.datosReporteResumen.data.porcentaje_total || 0).toFixed(1)}%
                               </Text>
                               <Text style={[styles.ambitosCell, { flex: 1.5, borderRightWidth: 0, textAlign: 'center', fontWeight: 'bold' }]}>
                                   {determinarLogroEquivalente(parseFloat(reportes.datosReporteResumen.data.porcentaje_total || 0))}
                               </Text>
                           </View>
                       )}
                   </View>
               </View>
           </Page>
       </Document>
   );
};

const Reportes = () => {
   const [ninosConEvaluaciones, setNinosConEvaluaciones] = useState([]);
   const [evaluacionesNino, setEvaluacionesNino] = useState(null);
   const [evaluacionSeleccionada, setEvaluacionSeleccionada] = useState(null);
   const [reportes, setReportes] = useState({});
   const [ninoSeleccionado, setNinoSeleccionado] = useState(null);
   const [centro, setCentro] = useState(null);
   const [searchTerm, setSearchTerm] = useState('');
   const [dateFilter, setDateFilter] = useState('');
   const [ninosOriginal, setNinosOriginal] = useState([]);
   const [fechasDisponibles, setFechasDisponibles] = useState([]);

   useEffect(() => {
       cargarNinosConEvaluaciones();
       cargarDatosCentro();
   }, []);

   useEffect(() => {
       filtrarNinos();
   }, [searchTerm, dateFilter, ninosOriginal]);

   const cargarDatosCentro = async () => {
       try {
           const response = await obtenerCentro();
           if (response.success) {
               setCentro(response.data[0]);
           }
       } catch (error) {
           console.error('Error al cargar datos del centro:', error);
       }
   };

   const cargarNinosConEvaluaciones = async () => {
       try {
           const response = await obtenerEvaluaciones();
           if (response.success) {
               const evaluacionesPorNino = response.data.reduce((acc, evaluacion) => {
                   const { nino } = evaluacion;
                   if (!acc[nino.id_child]) {
                       acc[nino.id_child] = {
                           id_child: nino.id_child,
                           nombre: nino.nombre,
                           apellidos: nino.apellidos,
                           evaluaciones: []
                       };
                   }
                   acc[nino.id_child].evaluaciones.push(evaluacion);
                   return acc;
               }, {});

               const ninosConEval = Object.values(evaluacionesPorNino)
                   .filter(nino => nino.evaluaciones.length > 0);

               // Extraer fechas únicas de todas las evaluaciones
               const fechasUnicas = [...new Set(
                   response.data.map(evaluacion => {
                       const fecha = new Date(evaluacion.fecha);
                       fecha.setMinutes(fecha.getMinutes() + fecha.getTimezoneOffset());
                       return formatearFechaLocal(fecha);
                   })
               )].sort();

               setFechasDisponibles(fechasUnicas);
               setNinosOriginal(ninosConEval);
               setNinosConEvaluaciones(ninosConEval);
           }
       } catch (error) {
           console.error('Error al cargar evaluaciones:', error);
       }
   };

   const filtrarNinos = () => {
       let ninosFiltrados = [...ninosOriginal];

       if (searchTerm) {
           ninosFiltrados = ninosFiltrados.filter(nino => 
               nino.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
               nino.apellidos.toLowerCase().includes(searchTerm.toLowerCase())
           );
       }

       if (dateFilter) {
           ninosFiltrados = ninosFiltrados.map(nino => ({
               ...nino,
               evaluaciones: nino.evaluaciones.filter(evaluacion => {
                   const fechaEvaluacion = new Date(evaluacion.fecha);
                   fechaEvaluacion.setMinutes(fechaEvaluacion.getMinutes() + fechaEvaluacion.getTimezoneOffset());
                   const fechaEvaluacionStr = formatearFechaLocal(fechaEvaluacion);
                   return fechaEvaluacionStr === dateFilter;
               })
           })).filter(nino => nino.evaluaciones.length > 0);
       }

       setNinosConEvaluaciones(ninosFiltrados);
   };

   const limpiarFiltros = () => {
       setSearchTerm('');
       setDateFilter('');
       setNinoSeleccionado(null);
       setEvaluacionesNino(null);
       setEvaluacionSeleccionada(null);
       setReportes({});
   };

   const handleNinoClick = (nino) => {
       if (ninoSeleccionado === nino.id_child) {
           // Si el niño ya está seleccionado, lo deseleccionamos
           setNinoSeleccionado(null);
           setEvaluacionesNino(null);
           setEvaluacionSeleccionada(null);
           setReportes({});
       } else {
           // Seleccionamos el nuevo niño
           setNinoSeleccionado(nino.id_child);
           setEvaluacionesNino(nino.evaluaciones);
           setEvaluacionSeleccionada(null);
           setReportes({});
       }
   };

   const handleEvaluacionClick = async (evaluacionId) => {
       try {
           if (evaluacionSeleccionada === evaluacionId) {
               setEvaluacionSeleccionada(null);
               setReportes({});
           } else {
               setEvaluacionSeleccionada(evaluacionId);
               
               const [reporteProgreso, reporteResumen] = await Promise.all([
                   obtenerReporteProgreso(evaluacionId),
                   obtenerReporteResumen(evaluacionId)
               ]);

               setReportes({
                   [evaluacionId]: {
                       datosReporteProgreso: reporteProgreso,
                       datosReporteResumen: reporteResumen
                   }
               });
           }
       } catch (error) {
           console.error('Error al obtener los reportes:', error);
       }
   };

   const formatearFecha = (fecha) => {
       if (!fecha) return '';
       const fechaObj = new Date(fecha);
       // Ajustar la zona horaria
       fechaObj.setMinutes(fechaObj.getMinutes() + fechaObj.getTimezoneOffset());
       return fechaObj.toLocaleDateString('es-ES', {
           year: 'numeric',
           month: 'long',
           day: 'numeric'
       });
   };

   return (
       <div className="p-3 sm:p-4 md:p-6 bg-white rounded-lg shadow-md">
           <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-800 mb-4 sm:mb-6">Reportes de Evaluaciones</h2>
           
            {/* Filtros mejorados */}
            <div className="bg-gray-50 rounded-xl p-4 mb-6 border border-gray-200">
            <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                Filtros de búsqueda
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                    Buscar por nombre
                </label>
                <input
                    type="text"
                    placeholder="Ingrese nombre o apellido..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full p-2.5 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 text-sm"
                    autoComplete="off"
                />
                </div>
                <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                    Filtrar por fecha
                </label>
                <select
                    value={dateFilter}
                    onChange={(e) => setDateFilter(e.target.value)}
                    className="w-full p-2.5 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 text-sm"
                >
                    <option value="">Todas las fechas</option>
                    {fechasDisponibles.map((fecha) => (
                    <option key={fecha} value={fecha}>
                        {new Date(fecha + 'T00:00:00').toLocaleDateString('es-ES', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                        })}
                    </option>
                    ))}
                </select>
                </div>
            </div>
            {(searchTerm || dateFilter) && (
                <div className="mt-3 flex items-center justify-between">
                <span className="text-xs text-gray-500">
                    Filtros activos: {[
                    searchTerm ? `Nombre: ${searchTerm}` : null,
                    dateFilter ? `Fecha: ${dateFilter}` : null
                    ].filter(Boolean).join(' | ')}
                </span>
                <div className="flex items-center gap-4">
                    <span className="text-xs text-gray-500">
                    {ninosConEvaluaciones.length} resultado(s)
                    </span>
                    <button
                    onClick={limpiarFiltros}
                    className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                    >
                    Limpiar filtros
                    </button>
                </div>
                </div>
            )}
            </div>

           {ninosConEvaluaciones.length > 0 ? (
            <div className="p-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {ninosConEvaluaciones.map((nino) => (
                    <div
                        key={nino.id_child}
                        className={`bg-gray-50 rounded-lg p-4 cursor-pointer group border border-transparent hover:border-blue-200 hover:bg-blue-50 transition-colors shadow-sm ${
                        ninoSeleccionado === nino.id_child ? 'ring-2 ring-blue-500 border-blue-200' : ''
                        }`}
                        onClick={() => handleNinoClick(nino)}
                    >
                        <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                            <User className="w-4 h-4 text-gray-600 group-hover:text-blue-600 transition-colors" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 group-hover:text-blue-600 truncate transition-colors">
                            {nino.nombre} {nino.apellidos}
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                            {nino.evaluaciones.length} evaluación(es)
                            </p>
                        </div>
                        <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-blue-600 transition-colors" />
                        </div>
                    </div>
                    ))}
                </div>
                </div>

           ) : (
               <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
                   <div className="flex flex-col items-center justify-center gap-3 text-center">
                       <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                           <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                           </svg>
                       </div>
                       <div>
                           <p className="text-lg font-medium text-gray-900">
                               {(searchTerm || dateFilter) ? 'No se encontraron resultados' : 'No hay evaluaciones registradas'}
                           </p>
                           <p className="text-sm text-gray-500 mt-1">
                               {(searchTerm || dateFilter) ? 'Intente con otros criterios de búsqueda' : 'No se encontraron registros de evaluaciones para generar reportes'}
                           </p>
                       </div>
                   </div>
               </div>
           )}

           {evaluacionesNino && (
            <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mb-6">
  <h3 className="text-lg sm:text-xl font-semibold text-gray-700 mb-6">Evaluaciones Disponibles</h3>
  <div className="space-y-4">
    {evaluacionesNino.map((evaluacion) => {
      const isSelected = evaluacionSeleccionada === evaluacion.id_evaluacion;
      const tieneReporte = reportes[evaluacion.id_evaluacion];

      return (
        <div key={evaluacion.id_evaluacion} className="border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow">
          <div
            className={`flex flex-col sm:flex-row justify-between items-center gap-4 p-4 cursor-pointer rounded-lg
              ${isSelected ? 'bg-blue-50 border-blue-300' : 'bg-gray-50 hover:bg-gray-100'}`}
            onClick={() => handleEvaluacionClick(evaluacion.id_evaluacion)}
          >
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <CalendarDays className="w-5 h-5 text-blue-500 flex-shrink-0" />
              <p className="text-sm sm:text-base font-medium text-gray-700 truncate">
                Fecha: {formatearFecha(evaluacion.fecha)}
              </p>
            </div>

            {isSelected && tieneReporte && (
            <PDFDownloadLink
                document={
                <ReportePDF
                    centro={centro}
                    nino={evaluacion.nino}
                    evaluacion={evaluacion}
                    reportes={reportes[evaluacion.id_evaluacion]}
                />
                }
                fileName={`reporte_${evaluacion.nino.nombre}_${formatearFecha(evaluacion.fecha)}.pdf`}
                className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm whitespace-nowrap"
                onClick={(e) => e.stopPropagation()}
            >
                <FileDown className="w-5 h-5" />
                Descargar PDF
            </PDFDownloadLink>
            )}
          </div>

          {isSelected && tieneReporte && (
            <div className="p-5 sm:p-6 border-t border-gray-200 space-y-6 bg-white rounded-b-lg">
              {/* Datos del Niño */}
              <section>
                <h4 className="text-lg sm:text-xl font-semibold text-gray-800 mb-4">Datos del Niño</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <p className="text-xs sm:text-sm text-gray-600">Nombre:</p>
                    <p className="text-sm sm:text-base font-medium text-gray-900 truncate">{evaluacion.nino.nombre} {evaluacion.nino.apellidos}</p>
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm text-gray-600">Fecha de Evaluación:</p>
                    <p className="text-sm sm:text-base font-medium text-gray-900">{formatearFecha(evaluacion.fecha)}</p>
                  </div>
                </div>
              </section>

              {/* Gráficos de Progreso */}
              <section>
                <h4 className="text-lg sm:text-xl font-semibold text-gray-800 mb-4">Gráficos de Progreso</h4>
                <div className="overflow-x-auto">
                  <div className="min-w-[320px]">
                    <EvaluationCharts
                      reportes={reportes}
                      evaluacionId={evaluacionSeleccionada}
                    />
                  </div>
                </div>
              </section>


            </div>
          )}
        </div>
      );
    })}
  </div>
</div>
           )}
       </div>
   );
};

export default Reportes;